import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Briefcase, Calendar, Users, Bell, User, Clock, CheckCircle, 
  XCircle, Loader2, LogOut, Home, ClipboardList, BookOpen,
  Save, ChevronRight, AlertCircle, UserCheck, FileText
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeacherMobileApp = () => {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [searchParams] = useSearchParams();
  const [school, setSchool] = useState(null);
  
  // Login form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Data states
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [timetable, setTimetable] = useState([]);
  const [notices, setNotices] = useState([]);
  const [myAttendance, setMyAttendance] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [homework, setHomework] = useState([]);

  // Get school from URL param
  const schoolFromUrl = searchParams.get('school');
  const schoolCode = schoolFromUrl || localStorage.getItem('school_code') || null;

  // Fetch school info
  useEffect(() => {
    if (schoolCode) {
      localStorage.setItem('school_code', schoolCode);
      localStorage.setItem('login_source', `/${schoolCode}`);
      fetchSchoolInfo();
    }
  }, [schoolCode]);

  const fetchSchoolInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schools/public/${schoolCode}`);
      setSchool(response.data);
    } catch (error) {
      console.error('Failed to fetch school:', error);
    }
  };

  // Check saved session
  useEffect(() => {
    const savedToken = localStorage.getItem('teacher_app_token');
    const savedUser = localStorage.getItem('teacher_app_data');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsLoggedIn(true);
    }
  }, []);

  // Fetch data when logged in
  useEffect(() => {
    if (isLoggedIn && token) {
      fetchInitialData();
    }
  }, [isLoggedIn, token]);

  // Fetch students when class changes
  useEffect(() => {
    if (selectedClass) {
      fetchStudents(selectedClass);
    }
  }, [selectedClass]);

  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { token: authToken, user: userData } = res.data;
      
      if (!['teacher', 'staff', 'school_admin'].includes(userData.role)) {
        toast.error('This app is for teachers and staff only');
        return;
      }
      
      setToken(authToken);
      setUser(userData);
      setIsLoggedIn(true);
      
      localStorage.setItem('teacher_app_token', authToken);
      localStorage.setItem('teacher_app_data', JSON.stringify(userData));
      
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken(null);
    setUser(null);
    localStorage.removeItem('teacher_app_token');
    localStorage.removeItem('teacher_app_data');
    toast.success('Logged out');
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchClasses(),
        fetchTimetable(),
        fetchNotices(),
        fetchMyAttendance(),
        fetchHomework()
      ]);
    } catch (error) {
      console.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() });
      const classList = res.data?.items || res.data || [];
      setClasses(classList);
      if (classList.length > 0 && !selectedClass) {
        setSelectedClass(classList[0].id);
      }
    } catch (error) {
      console.error('Classes fetch error');
    }
  };

  const fetchStudents = async (classId) => {
    try {
      const res = await axios.get(`${API_URL}/api/students?class_id=${classId}`, { headers: getAuthHeader() });
      const studentList = res.data?.items || res.data || [];
      setStudents(studentList);
      
      // Initialize attendance data
      const today = new Date().toISOString().split('T')[0];
      const initialAttendance = {};
      studentList.forEach(s => {
        initialAttendance[s.id] = { status: 'present', student_name: s.name };
      });
      setAttendanceData(initialAttendance);
    } catch (error) {
      console.error('Students fetch error');
    }
  };

  const fetchTimetable = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/timetable/my`, { headers: getAuthHeader() });
      setTimetable(res.data || []);
      
      // Filter today's schedule
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
      const todayClasses = (res.data || []).filter(t => t.day === today);
      setTodaySchedule(todayClasses);
    } catch (error) {
      console.error('Timetable fetch error');
    }
  };

  const fetchNotices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notices`, { headers: getAuthHeader() });
      setNotices((res.data?.items || res.data || []).slice(0, 10));
    } catch (error) {
      console.error('Notices fetch error');
    }
  };

  const fetchMyAttendance = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/my-attendance`, { headers: getAuthHeader() });
      setMyAttendance(res.data?.records || res.data || []);
    } catch (error) {
      console.error('My attendance fetch error');
    }
  };

  const fetchHomework = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/homework`, { headers: getAuthHeader() });
      setHomework(res.data?.items || res.data || []);
    } catch (error) {
      console.error('Homework fetch error');
    }
  };

  const toggleAttendance = (studentId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], status }
    }));
  };

  const saveAttendance = async () => {
    if (!selectedClass) {
      toast.error('Please select a class');
      return;
    }

    setSavingAttendance(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const records = Object.entries(attendanceData).map(([studentId, data]) => ({
        student_id: studentId,
        student_name: data.student_name,
        status: data.status,
        date: today,
        class_id: selectedClass,
        attendance_type: 'daily'
      }));

      await axios.post(`${API_URL}/api/attendance/bulk`, {
        class_id: selectedClass,
        date: today,
        records
      }, { headers: getAuthHeader() });

      toast.success(`Attendance saved for ${records.length} students`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save attendance');
    } finally {
      setSavingAttendance(false);
    }
  };

  const presentCount = Object.values(attendanceData).filter(a => a.status === 'present').length;
  const absentCount = Object.values(attendanceData).filter(a => a.status === 'absent').length;

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            {school && (school.logo_url || school.logo) ? (
              <div className="bg-white p-3 rounded-2xl inline-block mb-4">
                <img src={school.logo_url || school.logo} alt={school.name} className="h-16 w-16 object-contain" />
              </div>
            ) : (
              <div className="bg-white/20 p-4 rounded-full inline-block mb-4">
                <Briefcase className="h-12 w-12 text-white" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-white">{school?.name || 'Teacher / Staff App'}</h1>
            <p className="text-emerald-100 mt-2">Manage your classes and attendance</p>
          </div>

          <Card className="shadow-2xl">
            <CardHeader>
              <CardTitle>Login</CardTitle>
              <CardDescription>Enter your school credentials</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="teacher@school.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loginLoading}>
                  {loginLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Logging in...</>
                  ) : (
                    'Login'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-emerald-100 text-sm mt-6">
            Cloud Campus Schools
          </p>
        </motion.div>
      </div>
    );
  }

  // Main App
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 pb-16">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-emerald-100">Welcome back,</p>
            <h1 className="text-xl font-bold">{user?.name}</h1>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white hover:bg-white/20">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex items-center gap-2 text-sm text-emerald-100">
          <Badge variant="secondary" className="bg-white/20 text-white capitalize">
            {user?.role}
          </Badge>
          {user?.department && <span>• {user.department}</span>}
        </div>
      </div>

      {/* Content Area */}
      <div className="px-4 -mt-12">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Today's Classes</p>
                    <p className="text-2xl font-bold text-emerald-600">{todaySchedule.length}</p>
                  </div>
                  <div className="h-10 w-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Total Classes</p>
                    <p className="text-2xl font-bold text-blue-600">{classes.length}</p>
                  </div>
                  <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tab Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* Home Tab */}
          <TabsContent value="home" className="space-y-4 mt-0">
            {/* Today's Schedule */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Today's Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todaySchedule.length > 0 ? (
                  <div className="space-y-2">
                    {todaySchedule.map((slot, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{slot.subject}</p>
                          <p className="text-xs text-muted-foreground">{slot.class_name}</p>
                        </div>
                        <Badge variant="outline">{slot.start_time} - {slot.end_time}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No classes scheduled today</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Notices */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="h-4 w-4" /> Recent Notices
                </CardTitle>
              </CardHeader>
              <CardContent>
                {notices.length > 0 ? (
                  <div className="space-y-2">
                    {notices.slice(0, 3).map((notice, idx) => (
                      <div key={idx} className="p-2 bg-muted/50 rounded-lg">
                        <p className="font-medium text-sm">{notice.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{notice.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No notices</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="space-y-4 mt-0">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <UserCheck className="h-4 w-4" /> Mark Attendance
                </CardTitle>
                <CardDescription>Select class and mark attendance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map(cls => (
                      <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedClass && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600 font-medium">Present: {presentCount}</span>
                      <span className="text-red-600 font-medium">Absent: {absentCount}</span>
                      <span className="text-muted-foreground">Total: {students.length}</span>
                    </div>

                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {students.map(student => (
                        <div key={student.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{student.name}</p>
                            <p className="text-xs text-muted-foreground">Roll: {student.roll_number}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={attendanceData[student.id]?.status === 'present' ? 'default' : 'outline'}
                              className={attendanceData[student.id]?.status === 'present' ? 'bg-green-600' : ''}
                              onClick={() => toggleAttendance(student.id, 'present')}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant={attendanceData[student.id]?.status === 'absent' ? 'destructive' : 'outline'}
                              onClick={() => toggleAttendance(student.id, 'absent')}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700" 
                      onClick={saveAttendance}
                      disabled={savingAttendance || students.length === 0}
                    >
                      {savingAttendance ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="h-4 w-4 mr-2" /> Save Attendance</>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Attendance Tab */}
          <TabsContent value="myattendance" className="space-y-4 mt-0">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">My Attendance</CardTitle>
              </CardHeader>
              <CardContent>
                {myAttendance.length > 0 ? (
                  <div className="space-y-2">
                    {myAttendance.slice(0, 15).map((record, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {record.status === 'present' ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          <span className="text-sm">
                            {new Date(record.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                        <div className="text-right text-xs">
                          {record.check_in && <p>In: {record.check_in}</p>}
                          {record.check_out && <p>Out: {record.check_out}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No attendance records</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Homework Tab */}
          <TabsContent value="homework" className="space-y-4 mt-0">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BookOpen className="h-4 w-4" /> Assigned Homework
                </CardTitle>
              </CardHeader>
              <CardContent>
                {homework.length > 0 ? (
                  <div className="space-y-2">
                    {homework.map((hw, idx) => (
                      <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-sm">{hw.title}</p>
                          <Badge variant="outline" className="text-xs">{hw.class_name}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{hw.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Due: {new Date(hw.due_date).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No homework assigned</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t shadow-lg">
        <div className="flex justify-around py-2">
          {[
            { id: 'home', icon: Home, label: 'Home' },
            { id: 'attendance', icon: UserCheck, label: 'Mark' },
            { id: 'myattendance', icon: Calendar, label: 'My Att.' },
            { id: 'homework', icon: BookOpen, label: 'Homework' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center p-2 ${activeTab === tab.id ? 'text-emerald-600' : 'text-muted-foreground'}`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="text-xs mt-1">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeacherMobileApp;
